import { useEffect, useRef, useState } from 'react';
import { api } from '@/auth/api';

/**
 * Booking-page readiness. Polls GET /v1/portal/prewarm-status until the host's
 * on-demand cert is minted and it serves — the "Building your booking page…"
 * interstitial runs for the ACTUAL warm time, never a scripted timer. Returns
 * `ready` and the measured `durationMs` (from enable → first ready) for the
 * funnel beacon.
 */
export function usePortalReady(host: string | null, enabled: boolean) {
  const [ready, setReady] = useState(false);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !host || ready) return;
    if (startedAt.current === null) startedAt.current = Date.now();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const r = await api<{ ready: boolean }>(
          `/v1/portal/prewarm-status?domain=${encodeURIComponent(host)}`,
        );
        if (cancelled) return;
        if (r.ready) {
          setDurationMs(Date.now() - (startedAt.current ?? Date.now()));
          setReady(true);
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (!cancelled) timer = setTimeout(poll, 1200);
    };
    void poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [host, enabled, ready]);

  return { ready, durationMs };
}
